import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Upload, 
  User, 
  Image as ImageIcon, 
  Palette, 
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles
} from "lucide-react";

const NewShoot = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Step 1: Product Upload
  const [productName, setProductName] = useState("");
  const [productCategory, setProductCategory] = useState("fashion");
  const [productImage, setProductImage] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // Step 2: Model Selection
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelFilters, setModelFilters] = useState({
    gender: "",
    age_group: "",
    complexion: "",
    hair: ""
  });
  
  // Step 3: Background & Pose
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedPoses, setSelectedPoses] = useState(["front_pose"]);
  const [videoRequired, setVideoRequired] = useState(true);
  const [bgCategory, setBgCategory] = useState("");
  
  // SKU ID for the shoot
  const [skuId, setSkuId] = useState(null);

  useEffect(() => {
    if (step === 2) fetchModels();
    if (step === 3) fetchBackgrounds();
  }, [step, modelFilters, bgCategory]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (modelFilters.gender) params.append("gender", modelFilters.gender);
      if (modelFilters.age_group) params.append("age_group", modelFilters.age_group);
      if (modelFilters.complexion) params.append("complexion", modelFilters.complexion);
      if (modelFilters.hair) params.append("hair", modelFilters.hair);
      
      const response = await axios.get(`${API}/models?${params}`, { withCredentials: true });
      setModels(response.data);
    } catch (error) {
      toast.error("Failed to load models");
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgrounds = async () => {
    setLoading(true);
    try {
      const params = bgCategory ? `?category=${bgCategory}` : "";
      const response = await axios.get(`${API}/backgrounds${params}`, { withCredentials: true });
      setBackgrounds(response.data);
    } catch (error) {
      toast.error("Failed to load backgrounds");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    setProductImage(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setProductImageUrl(response.data.url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
      setProductImage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleStep1Submit = async () => {
    if (!productName.trim()) {
      toast.error("Please enter a product name");
      return;
    }
    if (!productImageUrl) {
      toast.error("Please upload a product image");
      return;
    }
    
    setLoading(true);
    try {
      // Generate a unique SKU code from product name
      const skuCode = `SKU-${productName.replace(/\s+/g, '-').toUpperCase().slice(0, 10)}-${Date.now().toString(36).toUpperCase()}`;
      
      // Create SKU first
      const skuResponse = await axios.post(`${API}/skus`, {
        sku_code: skuCode,
        sku_name: productName,
        category: productCategory
      }, { withCredentials: true });
      
      const newSkuId = skuResponse.data.sku_id;
      setSkuId(newSkuId);
      
      // Then add the uploaded image to the SKU as 'front' image
      await axios.post(`${API}/skus/${newSkuId}/images`, {
        image_type: "front",
        image_url: productImageUrl,
        original_filename: productImage?.name || "product.jpg"
      }, { withCredentials: true });
      
      setStep(2);
    } catch (error) {
      console.error("SKU creation error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to create SKU");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = () => {
    if (!selectedModel) {
      toast.error("Please select a model");
      return;
    }
    setStep(3);
  };

  const handleStep3Submit = () => {
    if (!selectedBackground) {
      toast.error("Please select a background");
      return;
    }
    if (selectedPoses.length === 0) {
      toast.error("Please select at least one pose");
      return;
    }
    setStep(4);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/shoots`, {
        sku_id: skuId,
        model_id: selectedModel.model_id,
        background_id: selectedBackground.background_id,
        pose_set: selectedPoses,
        video_required: videoRequired
      }, { withCredentials: true });
      
      toast.success("AI Shoot started! Generation in progress...");
      navigate(`/shoots/${response.data.shoot_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start AI shoot");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePose = (pose) => {
    setSelectedPoses(prev => 
      prev.includes(pose) 
        ? prev.filter(p => p !== pose)
        : [...prev, pose]
    );
  };

  const poses = [
    { id: "front_pose", label: "Front Pose" },
    { id: "side_pose", label: "Side Pose" },
    { id: "walk_pose", label: "Walk Pose" },
    { id: "lifestyle_pose", label: "Lifestyle Pose" }
  ];

  const bgCategories = [
    { id: "", label: "All" },
    { id: "indoor", label: "Indoor" },
    { id: "outdoor", label: "Outdoor" },
    { id: "royal", label: "Royal" },
    { id: "nature", label: "Nature" },
    { id: "cosmetic", label: "Cosmetic" }
  ];

  return (
    <DashboardLayout>
      <div data-testid="new-shoot-page" className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-neutral-100 text-neutral-400'
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 mx-2 rounded-full transition-colors ${
                  step > s ? 'bg-indigo-600' : 'bg-neutral-100'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="border-neutral-100">
          {/* Step 1: Upload Product */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Upload className="w-5 h-5 text-indigo-600" />
                  Step 1: Upload Product
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="product-name">Product Name</Label>
                  <Input 
                    id="product-name"
                    data-testid="product-name-input"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Summer Floral Dress"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <div className="flex gap-4 mt-2">
                    <Button
                      type="button"
                      variant={productCategory === "fashion" ? "default" : "outline"}
                      onClick={() => setProductCategory("fashion")}
                      className={productCategory === "fashion" ? "bg-indigo-600" : ""}
                    >
                      Fashion
                    </Button>
                    <Button
                      type="button"
                      variant={productCategory === "cosmetic" ? "default" : "outline"}
                      onClick={() => setProductCategory("cosmetic")}
                      className={productCategory === "cosmetic" ? "bg-indigo-600" : ""}
                    >
                      Cosmetic
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Product Image</Label>
                  <div className="mt-2">
                    {productImageUrl ? (
                      <div className="relative inline-block">
                        <img 
                          src={productImageUrl.startsWith('/') ? `${window.location.origin}${productImageUrl}` : productImageUrl}
                          alt="Product"
                          className="w-48 h-48 object-cover rounded-xl"
                        />
                        <button 
                          onClick={() => { setProductImage(null); setProductImageUrl(""); }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label 
                        data-testid="upload-product-image"
                        className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-neutral-200 rounded-xl cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                            <p className="text-sm text-neutral-600">Click to upload product image</p>
                            <p className="text-xs text-neutral-400 mt-1">PNG, JPG up to 10MB</p>
                          </>
                        )}
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    data-testid="step1-next-btn"
                    onClick={handleStep1Submit}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Next: Select Model
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Select Model */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-600" />
                  Step 2: Select AI Model
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Gender</Label>
                    <select 
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={modelFilters.gender}
                      onChange={(e) => setModelFilters(prev => ({ ...prev, gender: e.target.value }))}
                    >
                      <option value="">All</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="kid">Kid</option>
                    </select>
                  </div>
                  <div>
                    <Label>Age Group</Label>
                    <select 
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={modelFilters.age_group}
                      onChange={(e) => setModelFilters(prev => ({ ...prev, age_group: e.target.value }))}
                    >
                      <option value="">All</option>
                      <option value="kids">Kids</option>
                      <option value="18-25">18-25</option>
                      <option value="26-35">26-35</option>
                      <option value="36-45">36-45</option>
                    </select>
                  </div>
                  <div>
                    <Label>Complexion</Label>
                    <select 
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={modelFilters.complexion}
                      onChange={(e) => setModelFilters(prev => ({ ...prev, complexion: e.target.value }))}
                    >
                      <option value="">All</option>
                      <option value="fair">Fair</option>
                      <option value="wheatish">Wheatish</option>
                      <option value="dusky">Dusky</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div>
                    <Label>Hair</Label>
                    <select 
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={modelFilters.hair}
                      onChange={(e) => setModelFilters(prev => ({ ...prev, hair: e.target.value }))}
                    >
                      <option value="">All</option>
                      <option value="short">Short</option>
                      <option value="long">Long</option>
                      <option value="curly">Curly</option>
                      <option value="straight">Straight</option>
                    </select>
                  </div>
                </div>

                {/* Model Grid */}
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                ) : models.length === 0 ? (
                  <div className="text-center py-12 text-neutral-500">
                    No models found with selected filters
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {models.map((model) => (
                      <div 
                        key={model.model_id}
                        data-testid={`model-card-${model.model_id}`}
                        onClick={() => setSelectedModel(model)}
                        className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                          selectedModel?.model_id === model.model_id 
                            ? 'border-indigo-600 ring-2 ring-indigo-600/20' 
                            : 'border-transparent hover:border-indigo-200'
                        }`}
                      >
                        <img 
                          src={model.preview_image_url}
                          alt={model.name}
                          className="w-full aspect-[3/4] object-cover"
                        />
                        <div className="p-3 bg-white">
                          <p className="font-medium text-neutral-900">{model.name}</p>
                          <p className="text-xs text-neutral-500 capitalize">
                            {model.gender} • {model.age_group} • {model.complexion}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    data-testid="step2-next-btn"
                    onClick={handleStep2Submit}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Next: Select Background
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Background & Pose */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Palette className="w-5 h-5 text-indigo-600" />
                  Step 3: Background & Poses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Background Categories */}
                <div>
                  <Label>Background Category</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bgCategories.map((cat) => (
                      <Button
                        key={cat.id}
                        type="button"
                        variant={bgCategory === cat.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBgCategory(cat.id)}
                        className={bgCategory === cat.id ? "bg-indigo-600" : ""}
                      >
                        {cat.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Background Grid */}
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {backgrounds.map((bg) => (
                      <div 
                        key={bg.background_id}
                        data-testid={`bg-card-${bg.background_id}`}
                        onClick={() => setSelectedBackground(bg)}
                        className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                          selectedBackground?.background_id === bg.background_id 
                            ? 'border-indigo-600 ring-2 ring-indigo-600/20' 
                            : 'border-transparent hover:border-indigo-200'
                        }`}
                      >
                        <img 
                          src={bg.preview_image_url}
                          alt={bg.name}
                          className="w-full aspect-video object-cover"
                        />
                        <div className="p-3 bg-white">
                          <p className="font-medium text-neutral-900 text-sm">{bg.name}</p>
                          <p className="text-xs text-neutral-500 capitalize">{bg.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pose Selection */}
                <div>
                  <Label>Select Poses (up to 4)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {poses.map((pose) => (
                      <div 
                        key={pose.id}
                        data-testid={`pose-${pose.id}`}
                        onClick={() => togglePose(pose.id)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedPoses.includes(pose.id)
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-neutral-200 hover:border-indigo-200'
                        }`}
                      >
                        <Checkbox checked={selectedPoses.includes(pose.id)} />
                        <span className="font-medium">{pose.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Option */}
                <div 
                  onClick={() => setVideoRequired(!videoRequired)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    videoRequired
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-neutral-200 hover:border-indigo-200'
                  }`}
                >
                  <Checkbox checked={videoRequired} />
                  <div>
                    <span className="font-medium">Generate Walking Video</span>
                    <p className="text-sm text-neutral-500">4-6 second walking/rotation video</p>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(2)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    data-testid="step3-next-btn"
                    onClick={handleStep3Submit}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Next: Review
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Review & Generate */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  Step 4: Review & Generate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Product */}
                  <div className="space-y-2">
                    <Label className="text-neutral-500">Product</Label>
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <img 
                        src={productImageUrl.startsWith('/') ? `${window.location.origin}${productImageUrl}` : productImageUrl}
                        alt={productName}
                        className="w-full aspect-square object-cover rounded-lg mb-3"
                      />
                      <p className="font-medium">{productName}</p>
                      <p className="text-sm text-neutral-500 capitalize">{productCategory}</p>
                    </div>
                  </div>

                  {/* Model */}
                  <div className="space-y-2">
                    <Label className="text-neutral-500">AI Model</Label>
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <img 
                        src={selectedModel?.preview_image_url}
                        alt={selectedModel?.name}
                        className="w-full aspect-square object-cover rounded-lg mb-3"
                      />
                      <p className="font-medium">{selectedModel?.name}</p>
                      <p className="text-sm text-neutral-500 capitalize">
                        {selectedModel?.gender} • {selectedModel?.age_group}
                      </p>
                    </div>
                  </div>

                  {/* Background */}
                  <div className="space-y-2">
                    <Label className="text-neutral-500">Background</Label>
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <img 
                        src={selectedBackground?.preview_image_url}
                        alt={selectedBackground?.name}
                        className="w-full aspect-video object-cover rounded-lg mb-3"
                      />
                      <p className="font-medium">{selectedBackground?.name}</p>
                      <p className="text-sm text-neutral-500 capitalize">{selectedBackground?.category}</p>
                    </div>
                  </div>
                </div>

                {/* Selected Options */}
                <div className="bg-neutral-50 rounded-xl p-4">
                  <Label className="text-neutral-500 mb-2 block">Generation Options</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedPoses.map(pose => (
                      <span key={pose} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        {pose.replace('_', ' ')}
                      </span>
                    ))}
                    {videoRequired && (
                      <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                        Walking Video
                      </span>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-indigo-50 rounded-xl p-4">
                  <p className="text-indigo-900 font-medium">This will generate:</p>
                  <ul className="mt-2 space-y-1 text-indigo-700">
                    <li>• {selectedPoses.length} high-quality images</li>
                    {videoRequired && <li>• 1 walking video (4-6 seconds)</li>}
                  </ul>
                </div>

                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(3)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    data-testid="generate-shoot-btn"
                    onClick={handleFinalSubmit}
                    disabled={submitting}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Starting Generation...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate AI Shoot
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default NewShoot;
